import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DevModeBanner from "@/components/DevModeBanner";

import Landing from "@/pages/Landing";
import Demo from "@/pages/Demo";
import LegalPage from "@/pages/Legal";
import Features from "@/pages/Features";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import Workflows from "@/pages/Workflows";
import WorkflowRunner from "@/pages/WorkflowRunner";
import AIAssistant from "@/pages/AIAssistant";
import BusinessFormation from "@/pages/BusinessFormation";
import ProgressTracker from "@/pages/ProgressTracker";
import ReadinessReport from "@/pages/ReadinessReport";
import Billing from "@/pages/Billing";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import DataExport from "@/pages/DataExport";
import Admin from "@/pages/Admin";
import BusinessCredit from "@/pages/BusinessCredit";
import Funding from "@/pages/Funding";
import Documents from "@/pages/Documents";
import Contracts from "@/pages/Contracts";
import ClientPortal from "@/pages/ClientPortal";
import Education from "@/pages/Education";
import Resources from "@/pages/Resources";
import Compliance from "@/pages/Compliance";
import Security from "@/pages/Security";
import Team from "@/pages/Team";
import TeamAccept from "@/pages/TeamAccept";
import Referrals from "@/pages/Referrals";
import FeatureRequests from "@/pages/FeatureRequests";
import Status from "@/pages/public/Status";
import Changelog from "@/pages/public/Changelog";
import Roadmap from "@/pages/public/Roadmap";
import Testimonials from "@/pages/public/Testimonials";
import PublicTool from "@/pages/tools/PublicTool";
import PublicToolsIndex from "@/pages/tools/PublicToolsIndex";
import ContentHub from "@/pages/learn/ContentHub";
import StartBusinessGuide from "@/pages/learn/StartBusinessGuide";
import DemoVideo from "@/pages/DemoVideo";
import BusinessMastery from "@/pages/education/BusinessMastery";
import BusinessMasteryPublic from "@/pages/BusinessMasteryPublic";
import Unsubscribe from "@/pages/Unsubscribe";
import NotFound from "@/pages/NotFound";
import ModuleGate from "@/components/ModuleGate";
import { ClientAuthProvider, useClientAuth } from "@/context/ClientAuthContext";
import ClientLayout from "@/components/layout/ClientLayout";
import ClientLogin from "@/pages/client/ClientLogin";
import ClientAcceptInvite from "@/pages/client/ClientAcceptInvite";
import ClientForgotPassword from "@/pages/client/ClientForgotPassword";
import ClientResetPassword from "@/pages/client/ClientResetPassword";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientProgress from "@/pages/client/ClientProgress";
import ClientMessages from "@/pages/client/ClientMessages";
import ClientDocuments from "@/pages/client/ClientDocuments";
import ClientNotifications from "@/pages/client/ClientNotifications";
import ClientAdvisor from "@/pages/client/ClientAdvisor";
import ClientBilling from "@/pages/client/ClientBilling";
import ClientSettings from "@/pages/client/ClientSettings";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ClientProtected({ children }) {
  const { client, loading } = useClientAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!client) return <Navigate to="/client/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/demo/video" element={<DemoVideo />} />
      <Route path="/business-mastery" element={<BusinessMasteryPublic />} />
      <Route path="/r/:token" element={<ReadinessReport />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/team/accept" element={<TeamAccept />} />
      <Route path="/status" element={<Status />} />
      <Route path="/changelog" element={<Changelog />} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/testimonials" element={<Testimonials />} />
      <Route path="/tools" element={<PublicToolsIndex />} />
      <Route path="/tools/:slug" element={<PublicTool />} />
      <Route path="/learn" element={<ContentHub />} />
      <Route path="/learn/how-to-start-a-business" element={<StartBusinessGuide />} />
      <Route path="/learn/:hub" element={<ContentHub />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route element={<Protected><DashboardLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/workflows/:wf" element={<WorkflowRunner />} />
        <Route path="/ai-assistant" element={<AIAssistant />} />
        <Route path="/formation" element={<ModuleGate moduleKey="formation"><BusinessFormation /></ModuleGate>} />
        <Route path="/progress" element={<ProgressTracker />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/team" element={<Team />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/feature-requests" element={<FeatureRequests />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/data-export" element={<DataExport />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/credit" element={<ModuleGate moduleKey="credit"><BusinessCredit /></ModuleGate>} />
        <Route path="/funding" element={<ModuleGate moduleKey="funding"><Funding /></ModuleGate>} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/contracts" element={<ModuleGate moduleKey="contracts"><Contracts /></ModuleGate>} />
        <Route path="/portal" element={<ModuleGate moduleKey="portal"><ClientPortal /></ModuleGate>} />
        <Route path="/education" element={<Education />} />
        <Route path="/education/business-mastery" element={<BusinessMastery />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/compliance" element={<ModuleGate moduleKey="compliance"><Compliance /></ModuleGate>} />
        <Route path="/security" element={<Security />} />
      </Route>
      <Route path="/client" element={<ClientAuthProvider><Outlet /></ClientAuthProvider>}>
        <Route index element={<Navigate to="/client/login" replace />} />
        <Route path="login" element={<ClientLogin />} />
        <Route path="accept-invite" element={<ClientAcceptInvite />} />
        <Route path="forgot-password" element={<ClientForgotPassword />} />
        <Route path="reset-password" element={<ClientResetPassword />} />
        <Route element={<ClientProtected><ClientLayout /></ClientProtected>}>
          <Route path="dashboard" element={<ClientDashboard />} />
          <Route path="progress" element={<ClientProgress />} />
          <Route path="messages" element={<ClientMessages />} />
          <Route path="documents" element={<ClientDocuments />} />
          <Route path="notifications" element={<ClientNotifications />} />
          <Route path="advisor" element={<ClientAdvisor />} />
          <Route path="billing" element={<ClientBilling />} />
          <Route path="settings" element={<ClientSettings />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <DevModeBanner />
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
